import React, { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { api } from '@/api/client';

export default function TransportDiagnostic({ streamId, conversation = false }) {
  const path = `/livestreams/${encodeURIComponent(streamId)}/${conversation ? 'conversation' : 'transport'}-diagnostic`;
  const [status, setStatus] = useState(null);
  const [evidence, setEvidence] = useState({});
  const [error, setError] = useState('');
  const current = useRef(null), attempted = useRef(false), mounted = useRef(true);
  const update = value => { if (mounted.current) setEvidence(e => ({ ...e, ...value })); };
  async function cleanup(sendStop = true) {
    const state = current.current;
    if (!state) return;
    current.current = null;
    clearTimeout(state.timer);
    clearInterval(state.statsTimer);
    for (const track of state.tracks) { try { track.detach(); } catch (_) {} }
    for (const el of state.elements) { el.pause(); el.srcObject = null; el.remove(); }
    try { await state.room.disconnect(); update({ disconnected: true }); }
    finally { if (sendStop) await api(`${state.path}/stop`, { auth: true, method: 'POST' }).catch(() => {}); }
  }
  useEffect(() => {
    mounted.current = true;
    api(path, { auth: true }).then(s => { if (mounted.current) setStatus(s); }).catch(() => {});
    return () => { mounted.current = false; cleanup(); };
    // A new room gets a new component from its parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  async function refresh() {
    try { setStatus(await api(path, { auth: true })); }
    catch (_) { setError('Status unavailable. No test was retried.'); }
  }
  async function start() {
    if (attempted.current || status?.status !== 'ready') return;
    attempted.current = true;
    const room = new Room({ adaptiveStream: false, dynacast: false,
      reconnectPolicy: { nextRetryDelayInMs: () => null } });
    const state = { room, elements: [], tracks: [], path, timer: null, statsTimer: null };
    current.current = state;
    setStatus({ status: 'starting' });
    // User gesture unlock. This never enables a local media track.
    room.startAudio().catch(() => {});
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (current.current !== state || track.kind !== 'audio') return;
      const el = track.attach(); state.elements.push(el);
      state.tracks.push(track);
      el.controls = false; document.body.appendChild(el);
      update({ remoteAudioSubscribed: true });
      el.addEventListener('playing', () => update({ mediaPlayingEvent: true }));
      el.play().then(() => update({ playResolved: true })).catch(() => setError('Click Enable test sound to allow browser playback.'));
      clearInterval(state.statsTimer);
      state.statsTimer = setInterval(async () => {
        try {
          const stats = await track.getReceiverStats();
          if (current.current === state && stats) update({ bytesReceived: stats.bytesReceived,
            packetsReceived: stats.packetsReceived, totalAudioEnergy: stats.totalAudioEnergy,
            mediaTime: el.currentTime, paused: el.paused });
        } catch (_) { /* Playback evidence remains visible; no network retry. */ }
      }, 500);
    });
    room.on(RoomEvent.Disconnected, () => { cleanup(false); refresh(); });
    state.timer = setTimeout(() => cleanup(), 30000);
    try {
      const session = await api(path, { auth: true, method: 'POST', timeoutMs: 10000 });
      if (current.current !== state) return;
      clearTimeout(state.timer);
      const remaining = Math.min(30000, session.deadline * 1000 - Date.now());
      if (remaining <= 0) { await cleanup(); return; }
      state.timer = setTimeout(() => cleanup(), remaining);
      await room.connect(session.server_url, session.participant_token, { autoSubscribe: true, maxRetries: 0 });
      if (current.current !== state) { await room.disconnect(); return; }
      update({ connected: true, localTracks: room.localParticipant.trackPublications.size });
      setStatus({ status: 'running' });
    } catch (_) {
      setError('Test failed or outcome uncertain. No automatic retry. Check status.');
      await cleanup();
    }
  }
  async function unlock() {
    const state = current.current;
    if (!state) return;
    try { await state.room.startAudio(); await Promise.all(state.elements.map(el => el.play())); setError(''); }
    catch (_) { setError('Browser playback is still blocked.'); }
  }
  if (!status) return null;
  return <section aria-label="Private room transport check" className="tt-panel p-4 space-y-3">
    <h2>{conversation ? 'Private synthetic conversation check' : 'Private room sound check'}</h2>
    <p>{conversation ? 'One paid synthetic conversation: fixed input up to 12 seconds, one AI attempt, one spoken reply up to 300 characters, 30 seconds total. No microphone, camera or recording. No retries.' : 'One quiet synthetic tone, up to 30 seconds. No microphone, camera, recording or AI calls. LiveKit usage may be charged.'}</p>
    <p role="status">{status.status}</p>
    <button onClick={start} disabled={attempted.current || status.status !== 'ready'}>{conversation ? 'Run approved conversation check' : 'Run approved room sound check'}</button>
    <button onClick={unlock}>Enable test sound</button>
    <button onClick={() => cleanup()}>Stop test sound</button>
    <button onClick={refresh}>Check transport result</button>
    <pre>{JSON.stringify({ ...evidence, server: status.result }, null, 2)}</pre>
    {error && <p role="alert">{error}</p>}
  </section>;
}
